'use client';

import React, { useState, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { Container, Row } from 'reactstrap';

// Import components
import ClientBanner from '@/components/Banners/ClientBanner/ClientBanner';
import ClientList from '@/components/Grids/ClientList/ClientList';
import ErrorBoundaryCheck from '@/components/ErrorBoundaryClass/ErrorBoundaryClass';

// import static assets
import CarouselButtons from '@/components/CarouselButtons/CarouselButtons';
import { useEnvContext } from '@/context/envContext';
import { useLangContext } from '@/context/langContext';
import { ParamsProps } from '@/libs/interfaceProps/paramsProps';
import { useTranslations } from 'next-intl';
import CustomBanner from '@/components/Banners/ClientBanner/CustomBanner';
import { CustomOrgProps } from '@/libs/interfaceProps/customOrgProps';
import { useCustomOrgContext } from '@/context/customOrgContext';
import { useOrgContext } from '@/context/orgContext';
import Skeleton from '@/components/Banners/ClientBanner/ClientBannerSkeleton';
import ClientLogoBanner from '@/components/Banners/ClientBanner/ClientLogoBanner';
import { pushGTMDisplayTags } from '@/libs/pushGTMTags/pushGTMTags';
interface OrgPageProps {
  userEmailData?: {
    email: string | false;
    accessCodeGA4: string;
  };
  params: ParamsProps;
  globalEnvConfig: any;
  customOrgIds: CustomOrgProps;
  orgData: any;
}
const Org = ({
  params,
  globalEnvConfig,
  customOrgIds,
  orgData,
  userEmailData
}: OrgPageProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Build a query string from an object of data
  const query = new URLSearchParams(searchParams);

  // The query string
  const queryString = query.toString();

  // UTM params
  const utmParams = queryString ? '?' + queryString : '';
  const orgId = params?.orgId;
  const lang = params?.lang;
  const [clickedId, setClickedId] = useState('all0');
  const [filterValue, setFilterValue] = useState('All');
  const [customTemplate, setCustomTemplate] = useState<boolean>(false);
  const {
    setCdnEnv,
    setWebUrl,
    setUserUrl,
    setChApi
  } = useEnvContext();
  const {
    setOrgData
  } = useOrgContext();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const {
    setLang
  } = useLangContext();
  const {
    setCustomOrg
  } = useCustomOrgContext();
  const appCode = userEmailData?.email ? '99' : '98';

  // This useEfect runs twice prevent this using useRef
  // This may not be required in production environments  if this is caused by
  // React 'StrictMode' which doesn't effect production, remove this before eng deployment to test.
  const hasRun = React.useRef(false);
  useEffect(() => {
    if (!hasRun.current) {
      pushGTMDisplayTags({
        appCode: appCode,
        // page with no reg form, user not logged in | could be logged in so needs check for userEmail loggedin 98|99
        contentID: 'NONE',
        contentType: 'NONE',
        pageType: 'Org',
        memberStatus: 'NOT_MEMBER',
        accessStatus: 'NONE',
        assetType: 'NONE',
        orgID: orgId,
        orgName: orgData?.orgName
      });
      hasRun.current = true;
    }
  });
  useEffect(() => {
    setCustomTemplate(customOrgIds?.customTemplate);
    if (orgData) {
      setOrgData(orgData);
      setIsLoading(false);
    }
    setCdnEnv(globalEnvConfig?.NEXT_PUBLIC_CDN_ENV);
    setWebUrl(globalEnvConfig?.NEXT_PUBLIC_WEB_URL);
    setUserUrl(globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL);
    setChApi(globalEnvConfig?.NEXT_PUBLIC_CH_API);
    setLang(lang);
    setCustomOrg(customOrgIds);
  }, [globalEnvConfig?.NEXT_PUBLIC_CDN_ENV, globalEnvConfig?.NEXT_PUBLIC_CH_API, globalEnvConfig?.NEXT_PUBLIC_USER_TT_URL, globalEnvConfig?.NEXT_PUBLIC_WEB_URL, lang, setCdnEnv, setWebUrl, setUserUrl, setChApi, setLang, customOrgIds, setCustomOrg, orgData, setOrgData]);
  const handleClick = async (event: React.ChangeEvent<any>) => {
    // Set the click ID .
    setClickedId(event.target.id);
    // Set the filter value to fetch the talks based on the values.
    setFilterValue(event.target.value);
  };
  let rootUrl = pathname;
  if (params.peId) {
    rootUrl = rootUrl.replace(`/${params.peId}`, '');
  }
  rootUrl = rootUrl.replace(`/${params.orgId}`, '');
  let customerLinks = false;
  if (pathname.includes('/c/')) {
    customerLinks = true;
  }
  const t = useTranslations('Org');
  return <ErrorBoundaryCheck data-sentry-element="ErrorBoundaryCheck" data-sentry-component="Org" data-sentry-source-file="org.tsx">
   {isLoading ? <Skeleton /> : <>
     {orgData?.hasCustomBanner ? <>
       <ClientLogoBanner // client logo banner
        orgId={orgId} lang={lang} t={t} />
      </> : <>
       {customTemplate ? <CustomBanner orgId={orgId} lang={lang} t={t} customTemplateName={customOrgIds?.customTemplateName} /> : <ClientBanner orgId={orgId} lang={lang} t={t} />}
      </>}
    </>}

   <Container data-sentry-element="Container" data-sentry-source-file="org.tsx">
    <Row data-sentry-element="Row" data-sentry-source-file="org.tsx">
     <CarouselButtons orgId={orgId} onClick={handleClick} buttonId={clickedId} lang={lang} data-sentry-element="CarouselButtons" data-sentry-source-file="org.tsx" />
    </Row>
   </Container>
   <Container className={'client-list-container'} data-sentry-element="Container" data-sentry-source-file="org.tsx">
    <ClientList orgId={orgId} category={filterValue} rootUrl={rootUrl} customerLinks={customerLinks} utmParams={utmParams} peId={params?.peId} lang={lang} data-sentry-element="ClientList" data-sentry-source-file="org.tsx" />
   </Container>
  </ErrorBoundaryCheck>;
};
export default Org;